import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useExtendDeadline } from '../hooks/useBankTransfers';
import type { BankTransferQueueItem } from '../types/bankTransfer';
import { isAxiosError } from 'axios';

interface ExtendDeadlineDialogProps {
  readonly item: BankTransferQueueItem;
  readonly onClose: () => void;
  readonly onSuccess: (message: string) => void;
}

const addDays = (isoDateTime: string | null, days: number): string => {
  const base = isoDateTime ? new Date(isoDateTime) : new Date();
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 16);
};

export const ExtendDeadlineDialog = ({ item, onClose, onSuccess }: ExtendDeadlineDialogProps) => {
  const { t } = useTranslation('forms');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [newDeadline, setNewDeadline] = useState(() => addDays(item.transferDueAt, 3));
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [limitError, setLimitError] = useState(false);

  const mutation = useExtendDeadline();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLimitError(false);

    if (reason.trim().length === 0) {
      setReasonError(t('extendDeadline.reasonRequired'));
      return;
    }
    if (reason.trim().length > 1000) {
      setReasonError(t('extendDeadline.reasonTooLong'));
      return;
    }
    setReasonError('');

    mutation.mutate(
      {
        bookingId: item.bookingId,
        request: {
          stage: item.expectedStage,
          newDeadline: new Date(newDeadline).toISOString(),
          reason: reason.trim(),
          expectedProcessVersion: item.processVersion,
        },
        idempotencyKey,
      },
      {
        onSuccess: () => {
          onSuccess(t('extendDeadline.successToast'));
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 400) {
            setLimitError(true);
          } else if (isAxiosError(err) && err.response?.status === 409) {
            onClose();
            onSuccess(t('extendDeadline.staleVersionToast'));
          }
        },
      },
    );
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">
            {t('extendDeadline.dialogTitle')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Stage (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('extendDeadline.stage')}
            </label>
            <input
              type="text"
              value={item.expectedStage}
              readOnly
              className="w-full border border-gray-100 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* New deadline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('extendDeadline.newDeadline')}
            </label>
            <input
              type="datetime-local"
              value={newDeadline}
              onChange={e => setNewDeadline(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            {limitError && (
              <p className="mt-1 text-xs text-red-600">{t('extendDeadline.limitExceeded')}</p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('extendDeadline.reason')}
            </label>
            <textarea
              value={reason}
              onChange={e => {
                setReason(e.target.value);
                if (reasonError) setReasonError('');
              }}
              placeholder={t('extendDeadline.reasonPlaceholder')}
              maxLength={1000}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <p className="text-xs text-gray-400 text-right mt-0.5">{reason.length}/1000</p>
            {reasonError && <p className="text-xs text-red-600 mt-0.5">{reasonError}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 text-sm font-medium px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {mutation.isPending ? t('extendDeadline.submitting') : t('extendDeadline.submit')}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('extendDeadline.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
